<template>
  <div class="ba-actions">
    <betriebsanweisungProofAction
      :item="item"
      :config="config"
      @reloadData="$emit('reloadData')"
    />
    <v-tooltip v-if="showAddToTemplates && canManageStructureHere" bottom>
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" v-on="on" @click.stop="addToTemplates">
          mdi-file-plus
        </v-icon>
      </template>
      <span>{{ $t("betriebsanweisung.addToTemplates") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="!isReadOnlyUser">
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" v-on="on" @click.stop="openMoveDialog">
          content_paste_go
        </v-icon>
      </template>
      <span>{{ $t("operatingInstructions.move") }}</span>
    </v-tooltip>
    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" v-on="on" @click.stop="downloadPdf">
          picture_as_pdf
        </v-icon>
      </template>
      <span>{{ $t("general.download") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="canCreateHere">
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" v-on="on" @click.stop="copyItem">
          content_copy
        </v-icon>
      </template>
      <span>{{ $t("betriebsanweisung.copy") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="!isReadOnlyUser">
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" v-on="on" @click.stop="editItem"
          >edit</v-icon
        >
      </template>
      <span>{{ $t("betriebsanweisung.edit") }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="canDeleteHere">
      <template v-slot:activator="{ on }">
        <v-icon medium class="mr-2" v-on="on" @click.stop="openDeleteDialog">
          delete
        </v-icon>
      </template>
      <span>{{ $t("betriebsanweisung.delete") }}</span>
    </v-tooltip>
    <deleteDialog
      @triggerDelete="deleteItem"
      :item="item"
      :deleteDialog="deleteDialog"
    />
    <moveDialog
      :item="item"
      :moveDialog="moveDialog"
      :loading="moving"
      :array="instructionCategories"
      @cancel="moveDialog.open = false"
      @reload="reloadDataFunc"
      @confirm="handleMoveConfirm"
    />
  </div>
</template>

<script>
import betriebsanweisungProofAction from "./betriebsanweisungProofAction.vue";
import deleteDialog from "../../_utils/dataTableHelpers/actions/deleteDialog.vue";
import moveDialog from "../../_utils/dataTableHelpers/actions/moveDialog.vue";

export default {
  props: ["item", "config"],
  components: {
    deleteDialog,
    moveDialog,
    betriebsanweisungProofAction,
  },
  data: () => ({
    deleteDialog: {
      open: false,
      title: "betriebsanweisung.delete",
      description: "",
    },
    moveDialog: {
      open: false,
      title: "operatingInstructions.move",
      description: "",
      targetId: null,
    },
    moving: false,
    instructionCategories: [],
  }),
  computed: {
    showAddToTemplates() {
      if (this.item.sourceTemplate) return false;
      const sources = (this.config && this.config.templateSourceIds) || [];
      return !sources
        .map(String)
        .includes(String(this.item._id || this.item.id));
    },
  },
  methods: {
    routeParams(mode) {
      return {
        instructionCategoryId: this.$route.params.instructionCategoryId,
        instructionCategoryName: this.$route.params.instructionCategoryName,
        id: this.item._id || this.item.id,
        mode,
      };
    },
    async downloadPdf() {
      const r = await this.$store.dispatch(
        "getBetriebsanweisungPdf",
        this.item._id || this.item.id
      );
      if (r && r.base64) {
        const link = document.createElement("a");
        link.href = "data:application/pdf;base64," + r.base64;
        link.download =
          (this.item.title || this.$t("betriebsanweisung.fallbackFileName")) +
          ".pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    editItem() {
      this.$router.push({
        name: "betriebsanweisungForm",
        params: this.routeParams("edit"),
      });
    },
    async copyItem() {
      const copy = await this.$store.dispatch(
        "copyBetriebsanweisung",
        this.item._id || this.item.id
      );
      if (copy) this.reloadDataFunc();
    },
    async addToTemplates() {
      const full = await this.$store.dispatch(
        "getSingleBetriebsanweisung",
        this.item._id || this.item.id
      );
      if (!full) return;
      await this.$store.dispatch("createBetriebsanweisungTemplate", {
        name: full.title,
        type: full.type,
        scope: "local",
        sections: (full.sections || []).map((s) => ({
          key: s.key,
          title: s.title,
          bullets: [...(s.bullets || [])],
          symbols: [...(s.symbols || [])],
        })),
        sourceBetriebsanweisung: full._id || full.id,
      });
      this.reloadDataFunc();
    },
    async openMoveDialog() {
      this.moveDialog.open = true;
      this.moveDialog.targetId = this.item._id || this.item.id;
      this.moveDialog.description = this.$t(
        "operatingInstructions.movingOperatingInstruction",
        { name: this.item.title }
      );
      const result = await this.$store.dispatch("getAllInstructionCategories");
      const allCategories = Array.isArray(result)
        ? result
        : result?.items || result?.data || [];
      const currentId = this.$route.params.instructionCategoryId;
      this.instructionCategories = allCategories.filter(
        (loc) => String(loc.id) !== String(currentId)
      );
    },
    async handleMoveConfirm(targetId) {
      try {
        this.moving = true;
        await this.$store.dispatch("updateBetriebsanweisung", {
          id: this.item._id || this.item.id,
          instructionCategory: targetId,
        });
        this.moveDialog.open = false;
        this.reloadDataFunc();
      } finally {
        this.moving = false;
      }
    },
    openDeleteDialog() {
      this.deleteDialog.open = true;
      this.deleteDialog.description =
        this.$t("betriebsanweisung.delete") + ": " + this.item.title;
    },
    deleteItem(item) {
      this.$store.dispatch("deleteBetriebsanweisung", item).then(() => {
        this.reloadDataFunc();
        this.deleteDialog.open = false;
      });
    },
    reloadDataFunc() {
      this.$emit("reloadData");
    },
  },
};
</script>

<style scoped>
.ba-actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
}
</style>
